import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  EVENT_KINDS,
  EVENT_SCOPES,
  VISIBILITY,
  canSeeContent,
  getBlurbs,
  getVisibility,
  labelFor,
  listEvents,
  saveEvent,
} from "@hub";
import { useHubVisitor, withVisitor } from "../hubContext";

export default function Events() {
  const visitor = useHubVisitor();
  const [scope, setScope] = useState("");
  const [kind, setKind] = useState("");
  const [query, setQuery] = useState("");
  const [pulse, setPulse] = useState(0);
  const [draft, setDraft] = useState({
    title: "",
    kind: "workshop",
    scope: "local",
    starts_on: "",
    starts_at: "09:00",
    location: "",
    summary: "",
    visibility: "public",
    region: "San Diego County",
  });
  const visibility = getVisibility();
  const blurbs = getBlurbs();
  const canPost = ["agent", "broker", "admin"].includes(visitor.role);

  const events = useMemo(
    () =>
      listEvents()
        .filter((item) => canSeeContent(item.visibility, visitor.role))
        .filter((item) => !scope || item.scope === scope)
        .filter((item) => !kind || item.kind === kind)
        .filter((item) => {
          const hay = query.trim().toLowerCase();
          if (!hay) return true;
          return `${item.title} ${item.summary} ${item.location} ${item.region}`.toLowerCase().includes(hay);
        }),
    [visitor.role, scope, kind, query, pulse]
  );

  if (visibility.events === false) {
    return (
      <article>
        <h1>Events</h1>
        <p className="lead">The directory is hidden for now.</p>
      </article>
    );
  }

  function submit(event) {
    event.preventDefault();
    if (!draft.title.trim() || !draft.starts_on) return;
    saveEvent({
      ...draft,
      author_role: visitor.role,
    });
    setDraft({ ...draft, title: "", summary: "", location: "", starts_on: "" });
    setPulse((n) => n + 1);
  }

  return (
    <article>
      <h1>Events</h1>
      <p className="lead">{blurbs.events}</p>
      <p>
        <Link to={withVisitor("/calendar", visitor)}>Open the Journey calendar</Link>
        {" · "}
        <Link to={withVisitor("/community", visitor)}>Community board</Link>
      </p>
      <div className="hub-filters">
        <label>
          Search
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Title or place" />
        </label>
        <label>
          Scope
          <select value={scope} onChange={(event) => setScope(event.target.value)}>
            <option value="">Local to national</option>
            {EVENT_SCOPES.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Kind
          <select value={kind} onChange={(event) => setKind(event.target.value)}>
            <option value="">All kinds</option>
            {EVENT_KINDS.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="chip-row" aria-label="Event categories">
        <button type="button" className={!kind ? "chip is-active" : "chip"} onClick={() => setKind("")}>
          All
        </button>
        {EVENT_KINDS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={kind === item.id ? "chip is-active" : "chip"}
            onClick={() => setKind(item.id)}
          >
            {item.label}
          </button>
        ))}
      </p>
      <div className="card-grid">
        {events.map((item) => (
          <article key={item.id} className="card">
            <p className="muted">
              {item.starts_on}
              {item.starts_at ? ` · ${item.starts_at}` : ""} · {labelFor(EVENT_SCOPES, item.scope)} ·{" "}
              {labelFor(EVENT_KINDS, item.kind)}
            </p>
            <h2>{item.title}</h2>
            <p>
              {item.location}
              {item.region ? ` · ${item.region}` : ""}
            </p>
            <p>{item.summary}</p>
            <p>
              <Link to={withVisitor(`/events/${item.id}`, visitor)}>Event detail</Link>
            </p>
          </article>
        ))}
      </div>
      {!events.length ? (
        <p className="placeholder-note">No events in this view. Unsigned visitors see public listings only.</p>
      ) : null}

      {canPost ? (
        <form className="card hub-section" onSubmit={submit}>
          <h2>Post an event</h2>
          <p className="muted">
            Signed-in agents, brokers, and admin can add a listing on this origin. Admin Controls on
            REChecklist can edit the same pack later.
          </p>
          <label className="field">
            <span>Title</span>
            <input value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} required />
          </label>
          <div className="hub-filters">
            <label>
              Kind
              <select value={draft.kind} onChange={(event) => setDraft({ ...draft, kind: event.target.value })}>
                {EVENT_KINDS.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Scope
              <select value={draft.scope} onChange={(event) => setDraft({ ...draft, scope: event.target.value })}>
                {EVENT_SCOPES.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Visibility
              <select value={draft.visibility} onChange={(event) => setDraft({ ...draft, visibility: event.target.value })}>
                {VISIBILITY.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="hub-filters">
            <label>
              Date
              <input
                type="date"
                value={draft.starts_on}
                onChange={(event) => setDraft({ ...draft, starts_on: event.target.value })}
                required
              />
            </label>
            <label>
              Time
              <input type="time" value={draft.starts_at} onChange={(event) => setDraft({ ...draft, starts_at: event.target.value })} />
            </label>
            <label>
              Region
              <input value={draft.region} onChange={(event) => setDraft({ ...draft, region: event.target.value })} />
            </label>
          </div>
          <label className="field">
            <span>Location</span>
            <input value={draft.location} onChange={(event) => setDraft({ ...draft, location: event.target.value })} />
          </label>
          <label className="field">
            <span>Summary</span>
            <textarea rows={3} value={draft.summary} onChange={(event) => setDraft({ ...draft, summary: event.target.value })} />
          </label>
          <button className="btn" type="submit">
            Save event
          </button>
        </form>
      ) : (
        <p className="placeholder-note">
          Event posting for agents, brokers, and admin appears when you arrive from REChecklist Support
          with a role. Public visitors can still browse and open the calendar.
        </p>
      )}
    </article>
  );
}
